import * as React from 'react';
import { useEffect, useRef } from 'react';
import { CentralizedAnimatedLoader } from '@40seas/payments-shared';

/**
 * OAuth callback handler for social login (popup flow)
 * This page receives the OAuth code from the provider and sends it to the opener window
 * via postMessage and localStorage (as fallback for COOP issues).
 */
export const OAuthCallback = () => {
    // Capture URL params immediately during render, before any effects run
    // This ensures we get the params even if something else modifies the URL
    const paramsRef = useRef<URLSearchParams | null>(null);
    if (!paramsRef.current) {
        paramsRef.current = new URLSearchParams(window.location.search);
    }
    const params = paramsRef.current;

    // Effect runs only once on mount. params is accessed from paramsRef.current which
    // is captured during initial render, so it doesn't need to be in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        // Detect OAuth provider from URL parameters
        // Microsoft OAuth callbacks include 'session_state', Google does not
        const sessionState = params.get('session_state');
        const provider = sessionState !== null ? 'microsoft' : 'google';

        // Build the message to send
        // Note: state is included in both success and error cases per RFC 6749 Section 4.1.2.1
        // This allows consumers to correlate errors with pending requests and perform CSRF validation
        const message = error
            ? { type: 'OAUTH_CALLBACK', error: errorDescription || error, state, provider }
            : { type: 'OAUTH_CALLBACK', code, state, provider };

        // Method 1: Try postMessage to opener
        // Use same origin to prevent leaking sensitive OAuth code to other windows
        if (window.opener) {
            try {
                window.opener.postMessage(message, window.location.origin);
            } catch {
                // Silent fail - localStorage will be used as fallback
            }
        }

        // Method 2: Also set localStorage (backup for COOP issues)
        try {
            localStorage.setItem('oauth_callback_result', JSON.stringify(message));
        } catch {
            // Silent fail - postMessage should have succeeded
        }

        // Track timers for cleanup on unmount (prevents duplicate calls in Strict Mode)
        let redirectTimerId: ReturnType<typeof setTimeout>;

        // Close the popup after a short delay
        const closeTimerId = setTimeout(() => {
            window.close();

            // If we're still here after 1 second, we're not in a popup - redirect
            redirectTimerId = setTimeout(() => {
                // Redirect to root with the code params so auth-client AuthProvider can handle it
                const redirectUrl = new URL('/', window.location.origin);
                if (code) redirectUrl.searchParams.set('code', code);
                if (state) redirectUrl.searchParams.set('state', state);
                if (error) redirectUrl.searchParams.set('error', error);
                // Include provider so the callback handler knows which OAuth endpoint to use
                redirectUrl.searchParams.set('provider', provider);
                window.location.href = redirectUrl.toString();
            }, 1000);
        }, 200);

        // Cleanup: clear timers if component unmounts before they fire
        return () => {
            clearTimeout(closeTimerId);
            clearTimeout(redirectTimerId);
        };
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <CentralizedAnimatedLoader />
            <p style={{ marginTop: 20 }}>Completing authentication...</p>
        </div>
    );
};
